/**
 * 购物车满减活动提示 (BMSM - Buy More Save More)
 * data-show-info：是否渲染 .cart-bmsm-discount-info（进度与文案）
 * data-show-products：是否请求并渲染推荐商品
 */
class CartBmsmDiscount extends HTMLElement {
  constructor() {
    super();
    this.cartTotalPrice = this.dataset.cartTotalPrice;
    this.cartItemCount = this.dataset.cartItemCount;
    this.productId = this.dataset.productId;
    this.showInfo = this.dataset.showInfo === 'true';
    this.showProducts = this.dataset.showProducts === 'true';
    this.rootUrl = (this.dataset.rootUrl || (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) || '/').replace(/\/?$/, '/');
    const scriptEl = this.querySelector('script[type="application/json"]');
    if (!scriptEl) return;
    this.json = JSON.parse(scriptEl.textContent);
    const listStr = typeof this.json.discount_list === 'string' ? this.json.discount_list : String(this.json.discount_list || '');
    this.discountList = listStr.split(',').filter(Boolean).map((item) => {
      const parts = item.trim().split('-');
      const threshold = (parseFloat(parts[0]) || 0) * 100;
      const discount = (parseFloat(parts[1]) || 0) * 100;
      const active = Number(this.cartTotalPrice) >= threshold;
      return {
        threshold,
        discount,
        thresholdMoney: this._formatMoney(threshold),
        discountMoney: this._formatMoney(discount),
        active,
      };
    });
  }

  _formatMoney(amount, zero = false) {
    const format = typeof Shopify !== 'undefined' && Shopify.money_format ? Shopify.money_format : '${{amount}}';
    const money = typeof Shopify !== 'undefined' && Shopify.formatMoney
      ? Shopify.formatMoney(amount, format)
      : '$' + (amount / 100).toFixed(2);
    const str = typeof money === 'string' ? money : String(money);
    return !zero ? str.split('.')[0] : str;
  }

  /**
   * 最多只渲染四个档位
   * 窗口尽量包含：当前档前 1 档 + 当前档 + 当前档后 2 档
   */
  get renderList() {
    const list = this.discountList;
    const maxShow = 4;
    if (!list.length) return [];
    if (list.length <= maxShow) return list;

    let currentIndex = -1;
    for (let i = list.length - 1; i >= 0; i--) {
      if (list[i].active) {
        currentIndex = i;
        break;
      }
    }
    if (currentIndex < 0) {
      return list.slice(0, maxShow);
    }

    let startIndex = Math.max(0, currentIndex - 1);
    if (startIndex + maxShow > list.length) {
      startIndex = Math.max(0, list.length - maxShow);
    }
    return list.slice(startIndex, startIndex + maxShow);
  }

  connectedCallback() {
    if (Number(this.cartItemCount) === 0) return;
    if (!this.showInfo && !this.showProducts) return;
    this.render();
  }

  render() {
    const infoPart =
      this.showInfo && this.discountList.length
        ? `<div class="cart-bmsm-discount-info">${this.renderTextHTML}${this.renderListHTML}</div>`
        : '';
    const productsPart = this.showProducts
      ? `<div class="cart-bmsm-discount-products-wrap">${this._loadingHTML}</div>`
      : '';
    this.innerHTML = infoPart + productsPart;

    if (this.showProducts) {
      this.renderRecommendProducts();
    }
  }

  get _loadingHTML() {
    return `<div class="cart-bmsm-discount-products-loading" aria-hidden="true">
      <div class="cart-bmsm-discount-loading-spinner"></div>
      <span class="cart-bmsm-discount-loading-text">Loading...</span>
    </div>`;
  }

  get renderTextHTML() {
    if (!this.discountList.length) return '';
    const cartCents = Number(this.cartTotalPrice) || 0;
    const textTpl = this.json.text || 'Spend [buy_money] more to get [off_money] off.';
    const maxText = this.json.maximum_text || 'Congratulations! Maximum savings unlocked.';

    const nextTier = this.discountList.find((item) => !item.active);
    if (!nextTier) {
      return maxText;
    }

    const spendMoreCents = Math.max(0, nextTier.threshold - cartCents);
    const buyMoney = this._formatMoney(spendMoreCents, true);
    const offMoney = nextTier.discountMoney;

    return `<div class="cart-bmsm-discount-text">${textTpl
      .replace('[buy_money]', buyMoney)
      .replace('[off_money]', offMoney)}</div>`;
  }

  get renderListHTML() {
    if (!this.discountList.length) return '';
    let html = '';
    this.renderList.forEach((item) => {
      html += `
        <div class="cart-bmsm-discount-item ${item.active ? 'active' : ''}">
          <div class="cart-bmsm-discount-item-top">
            <div class="cart-bmsm-discount-item-line"></div>
            <div class="cart-bmsm-discount-item-moneys">
              <div class="cart-bmsm-discount-item-discount">-${item.discountMoney}</div>
              <div class="cart-bmsm-discount-item-threshold">${item.thresholdMoney}</div>
            </div>
          </div>
        </div>
      `;
    });
    return `<div class="cart-bmsm-discount-list">${html}</div>`;
  }

  async getRecommendProducts() {
    const url = `${this.rootUrl}recommendations/products?product_id=${this.productId}&limit=6&section_id=cart-bmsm-discount-products`;
    let html = await fetch(url + '&intent=complementary')
      .then((response) => response.text())
      .then((h) => {
        const doc = new DOMParser().parseFromString(h, 'text/html');
        const section = doc.querySelector('.shopify-section');
        return section ? section.innerHTML.trim() : '';
      });
    if (!html) {
      html = await fetch(url)
        .then((response) => response.text())
        .then((h) => {
          const doc = new DOMParser().parseFromString(h, 'text/html');
          const section = doc.querySelector('.shopify-section');
          return section ? section.innerHTML.trim() : '';
        });
    }
    return html;
  }

  async renderRecommendProducts() {
    const wrap = this.querySelector('.cart-bmsm-discount-products-wrap');
    if (!wrap) return;
    try {
      const html = await this.getRecommendProducts();
      wrap.innerHTML = html;
    } catch (e) {
      wrap.innerHTML = '';
    }
  }
}

if (!customElements.get('cart-bmsm-discount')) {
  customElements.define('cart-bmsm-discount', CartBmsmDiscount);
}
